import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import type { i18n } from 'i18next';
import { useAuth, type AuthAdapter, type RuntimeConfig } from '@eis/core';
import { AppProviders } from '../providers/AppProviders';
import { buildRouter } from './router';

/**
 * The router context is refreshed whenever the effective permission set changes, so guards and
 * nav filtering always evaluate against the current session — including right after a persona
 * switch in the mock adapter.
 */
function RouterWithAuthContext(): ReactNode {
  const { can, canAny, canAll, canGroups, status } = useAuth();
  const [router, setRouter] = useState<Awaited<ReturnType<typeof buildRouter>> | null>(null);

  // Module route trees are lazy chunks, so the router is assembled asynchronously once.
  useEffect(() => {
    let cancelled = false;
    void buildRouter().then((built) => {
      if (!cancelled) setRouter(built);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const context = useMemo(
    () => ({ permissions: { can, canAny, canAll, canGroups } }),
    [can, canAny, canAll, canGroups],
  );

  if (status === 'loading' || router === null) return null;

  return <RouterProvider router={router} context={context} />;
}

export function App({
  config,
  adapter,
  i18nInstance,
}: {
  readonly config: RuntimeConfig;
  readonly adapter: AuthAdapter;
  readonly i18nInstance: i18n;
}): ReactNode {
  return (
    <AppProviders config={config} adapter={adapter} i18nInstance={i18nInstance}>
      <RouterWithAuthContext />
    </AppProviders>
  );
}
